/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Injectable, InjectionToken } from '@angular/core';
import { Resource } from '@ngxp/rest';
import { Observable } from 'rxjs';
import { ApiListResourceService } from '../resource/api-list-resource.service';
import { ApiResourceService } from '../resource/api-resource.service';
import { LinkRelationName, ListItemResource, ListResourceServiceConfig, ResourceServiceConfig } from '../resource/resource.model';
import { ListResource, StateResource } from '../resource/resource.util';
import { BasedService } from './based.service';

@Injectable()
export abstract class ApiBasedService<
  B extends Resource,
  T extends Resource,
  LB extends Resource,
  LT extends ListResource,
  LI extends ListItemResource,
  R,
> extends BasedService<B, T, LB, LT, LI, ApiResourceService<B, T>, ApiListResourceService<LB, LT, LI>> {
  constructor() {
    super();
  }

  override _createResourceService(config: ResourceServiceConfig<B>): ApiResourceService<B, T> {
    return new ApiResourceService<B, T>(config);
  }

  override _createListResourceService(config: ListResourceServiceConfig<LB>): ApiListResourceService<LB, LT, LI> {
    return new ApiListResourceService<LB, LT, LI>(config);
  }

  public save(toSave: R): Observable<StateResource<T>> {
    return this._getResourceService().save(toSave);
  }

  public patch(toPatch: Partial<R>): Observable<StateResource<T>> {
    return this._getResourceService().patch(toPatch);
  }

  public mergePatch(toPatch: Partial<R>): Observable<StateResource<T>> {
    return this._getResourceService().mergePatch(toPatch);
  }

  public create<R>(toCreate: R): Observable<StateResource<LI>> {
    return this._getListResourceService().create(toCreate);
  }

  public delete(): Observable<StateResource<T>> {
    return this._getResourceService().delete();
  }

  public executeAction<P, A extends Resource = T>(linkRel: LinkRelationName, payload?: P): Observable<StateResource<A>> {
    return this._getResourceService().executeAction<P, A>(linkRel, payload);
  }
}

export const API_SERVICE: InjectionToken<ApiBasedService<Resource, Resource, Resource, ListResource, ListItemResource, any>> =
  new InjectionToken('ApiService - Token');
